import React, { useMemo, memo } from 'react';
import { MarkerIcon } from '@rsos/assets/icons';
import { Box } from '@rsos/components/capstone/base';
import { ESRI_GEOCODE_SERVICE_URL } from '@rsos/maps/interface/reverseGeocode/helpers';
import { RSOS_PLACE_SOURCE } from '@rsos/maps/interface/rsosGeocoder/rsosGeocoder';
import {
  ResultWrapper,
  StyledSecondaryAddr,
  RegionalAddr,
} from '../ConnectedUniversalSearch.styles';

function memoed(prevProps, nextProps) {
  // rerender if mouseCursor is or was aligned with the index
  return !(
    prevProps.index === prevProps.mouseCursor ||
    nextProps.index === nextProps.mouseCursor ||
    prevProps.suggestion.text !== nextProps.suggestion.text
  );
}

/**
 * StyledSuggestedAddress displays a suggestion from the /suggest endpoint.
 * The suggestion contains { text, magicKey, _serviceUrl, _token } - the full address
 * details are fetched when the user selects a suggestion using the magicKey.
 *
 * Parses the text to display:
 * - Street/Building name (bolded) - first part before comma
 * - City, State, Zip (smaller) - remaining parts after first comma
 *
 * Uses _serviceUrl to determine if it's a local GIS or global ESRI search.
 */
const StyledSuggestedAddress = memo(
  ({ suggestion, index, mouseCursor, handleSelectedAddress }) => {
    const { text, _serviceUrl } = suggestion || {};

    // Determine if this is a global (ESRI) or local GIS search
    const isGlobalSearch = useMemo(() => {
      return (
        _serviceUrl === ESRI_GEOCODE_SERVICE_URL ||
        _serviceUrl === RSOS_PLACE_SOURCE
      );
    }, [_serviceUrl]);

    // Parse the text to extract street address and regional address
    // Format is typically: "123 Main St, City, State 12345"
    const { streetAddr, regionalAddr } = useMemo(() => {
      if (!text) return { streetAddr: '', regionalAddr: '' };

      const firstCommaIndex = text.indexOf(',');
      if (firstCommaIndex === -1) {
        return { streetAddr: text, regionalAddr: '' };
      }

      return {
        streetAddr: text.substring(0, firstCommaIndex).trim(),
        regionalAddr: text.substring(firstCommaIndex + 1).trim(),
      };
    }, [text]);

    return (
      <ResultWrapper
        onClick={() => {
          handleSelectedAddress(suggestion);
        }}
        isHovered={mouseCursor === index}
        key={index}
        data-is-global={isGlobalSearch}
      >
        <Box horizontal p="0" m="0">
          <MarkerIcon width={16} height={16} />
          <Box p="0" m="0">
            {streetAddr && (
              <StyledSecondaryAddr isPrimary>{streetAddr}</StyledSecondaryAddr>
            )}
            {regionalAddr && (
              <Box horizontal>
                <RegionalAddr>{regionalAddr}</RegionalAddr>
              </Box>
            )}
          </Box>
        </Box>
      </ResultWrapper>
    );
  },
  memoed,
);

export default StyledSuggestedAddress;
